import React from 'react';
import { useWeb3 } from '@/hooks/useWeb3';
import type { VestingGrant } from '@/hooks/useTeamDistribution';

interface GrantsListProps {
  grants: VestingGrant[];
  onGrantSelect: (grant: VestingGrant) => void;
}

export const GrantsList: React.FC<GrantsListProps> = ({ grants, onGrantSelect }) => {
  const web3 = useWeb3();

  return (
    <div className="glass-effect border border-primary/10 rounded-2xl p-6 card-glow">
      <h3 className="text-lg font-semibold text-gray-200 mb-4">
        Your Grants ({grants.length})
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {grants.map((grant) => {
          const progress =
            grant.totalAmount > BigInt(0)
              ? (Number(grant.releasedAmount) / Number(grant.totalAmount)) * 100
              : 0;
          const claimableAmount = grant.releasableAmount || BigInt(0);
          const canClaimFromGrant = claimableAmount > BigInt(0);

          return (
            <button
              key={grant.id.toString()}
              onClick={() => onGrantSelect(grant)}
              className="bg-dark-lighter border border-primary/10 rounded-xl p-4 hover:border-primary/30 transition-all duration-300 text-left hover:bg-dark-lighter/80 cursor-pointer group"
            >
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-md font-semibold text-gradient-primary group-hover:text-primary-light">
                  Grant #{grant.id.toString()}
                </h4>
                {grant.isActive ? (
                  <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full border border-green-500/30">
                    Active
                  </span>
                ) : (
                  <span className="px-2 py-1 bg-gray-500/20 text-gray-400 text-xs rounded-full border border-gray-500/30">
                    Inactive
                  </span>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Total:</span>
                  <span className="text-gray-200 font-medium">
                    {web3.formatTokenAmount(grant.totalAmount)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Released:</span>
                  <span className="text-gray-200 font-medium">
                    {web3.formatTokenAmount(grant.releasedAmount)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Claimable:</span>
                  <span
                    className={`font-medium ${
                      canClaimFromGrant ? 'text-green-400' : 'text-gray-500'
                    }`}
                  >
                    {web3.formatTokenAmount(claimableAmount)}
                  </span>
                </div>

                {/* Progress bar */}
                <div className="mt-3">
                  <div className="flex mb-1 items-center justify-between">
                    <span className="text-xs font-semibold text-primary">
                      {progress.toFixed(2)}% Claimed
                    </span>
                  </div>
                  <div className="overflow-hidden h-2 text-xs flex rounded-full bg-dark">
                    <div
                      style={{ width: `${progress}%` }}
                      className="shadow-none flex flex-col text-center whitespace-nowrap text-dark justify-center bg-primary transition-all duration-500 rounded-full"
                    ></div>
                  </div>
                </div>

                <p className="text-xs text-gray-500 mt-2">
                  Click to view details and unlock schedule
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
